import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, ShieldCheck, KeyRound, ArrowRight, Gauge, Leaf } from 'lucide-react';
import OTPInput from '../components/OTPInput';
import { API_BASE_URL } from '../lib/api/client';
import { useAuth } from '../context/AuthContext';
import '../styles/auth.css';

// Guard against non-JSON responses
async function safeJson(res) {
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
        throw new Error('Server error — please try again in a moment.');
    }
    return res.json();
}

export default function LPULogin({ initialMode = 'register' }) {
    const [step, setStep] = useState(1); // 1: Info, 2: OTP, 3: Password
    const [fullName, setFullName] = useState('');
    const [emailPrefix, setEmailPrefix] = useState('');
    
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
    // New login state
    const [mode, setMode] = useState(initialMode); // 'register' or 'login'
    
    React.useEffect(() => {
        setMode(initialMode);
    }, [initialMode]);
    const [loginPassword, setLoginPassword] = useState('');
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const navigate = useNavigate();
    const { login } = useAuth();
    const lpuDomain = '@lpu.co.in';

    const getFullEmail = () => `${emailPrefix.trim()}${lpuDomain}`;

    // Smart @ handler: strips @ and shows suggestion popup
    const [showSuggestion, setShowSuggestion] = useState(false);
    const handleEmailPrefixChange = (val) => {
        if (val.includes('@')) {
            // Strip @ and everything after it, show popup
            setEmailPrefix(val.split('@')[0]);
            setShowSuggestion(true);
        } else {
            setEmailPrefix(val);
            setShowSuggestion(false);
        }
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        
        if (!fullName.trim()) {
            setError('Please enter your full name.');
            return;
        }
        if (!emailPrefix.trim()) {
            setError('Please enter your email prefix.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        
        const email = getFullEmail();

        try {
            // Step 1: Try to request OTP for forgot password
            let res = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            let data = await safeJson(res);
            
            // If user already exists, they still get the OTP and can reset their password to re-claim/upgrade their tier!
            if (res.ok) {
                setSuccess('Account recognized. Reset link (OTP) sent! Check your institutional email.');
                setStep(2);
                setLoading(false);
                return;
            }
            
            // If user doesn't exist (404), register them with a random password first
            if (res.status === 404 || (data.detail && data.detail.includes('No account found'))) {
                // Generate strong random password
                const randomPassword = 'Lpu!' + Math.random().toString(36).slice(-8) + 'A1';
                
                const regRes = await fetch(`${API_BASE_URL}/api/v1/auth/register`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        email: email,
                        password: randomPassword,
                        full_name: fullName,
                        organization: 'Lovely Professional University'
                    }),
                });
                
                if (!regRes.ok) {
                    const regData = await safeJson(regRes);
                    throw new Error(regData.detail || 'Failed to create your account.');
                }
                
                // Wait briefly for db flush, then request OTP again
                await new Promise(resolve => setTimeout(resolve, 500));
                
                res = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password/request-otp`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email }),
                });
                data = await safeJson(res);
                
                if (!res.ok) {
                    throw new Error(data.detail || 'Failed to send OTP. Please try again.');
                }
            } else if (!res.ok) {
                throw new Error(data.detail || 'Failed to send OTP. Please try again.');
            }

            setSuccess('Reset link (OTP) sent! Check your institutional email.');
            setStep(2);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError('Please enter the 6-digit OTP.');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: getFullEmail(), otp }),
            });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data.detail || 'OTP verification failed. Please try again.');
            setSuccess('OTP verified! Now set your secure password.');
            setStep(3);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        
        setLoading(true);
        setError('');
        setSuccess('');
        
        const email = getFullEmail();
        
        try {
            // 1. Reset the password
            const resetRes = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp, new_password: newPassword }),
            });
            const resetData = await safeJson(resetRes);
            if (!resetRes.ok) {
                let errMsg = 'Password setup failed.';
                if (Array.isArray(resetData.detail)) {
                    errMsg = resetData.detail.map(e => e.msg || e.message || '').join(' ');
                } else if (typeof resetData.detail === 'string') {
                    errMsg = resetData.detail;
                }
                throw new Error(errMsg);
            }
            
            // 2. Automatically log them in using useAuth's login method
            try {
                await login(email, newPassword);
                setSuccess('Welcome to TeachGenie! Redirecting to your dashboard...');
                setTimeout(() => navigate('/generator'), 1500);
            } catch (loginErr) {
                // If login fails, redirect to LPU login page instead of normal login page
                setSuccess('Password set successfully! Redirecting to LPU login...');
                setTimeout(() => navigate('/lpu/login'), 2000);
            }
            
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: getFullEmail() }),
            });
            const data = await safeJson(res);
            if (!res.ok) throw new Error(data.detail || 'Failed to resend OTP.');
            setSuccess('A new OTP has been sent to your LPU email.');
            setOtp('');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!emailPrefix.trim()) {
            setError('Please enter your email prefix.');
            return;
        }
        if (!loginPassword) {
            setError('Please enter your password.');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const email = getFullEmail();

        try {
            await login(email, loginPassword);
            
            setSuccess('Login successful! Redirecting...');
            setTimeout(() => navigate('/generator'), 1000);
        } catch (err) {
            const raw = (err.message || '').toLowerCase();
            const code = err.statusCode;
            let errMsg = '⚠️ Login failed. Please check your details and try again.';
            
            if (code === 401 || raw.includes('incorrect password') || raw.includes('invalid') || raw.includes('credentials'))
                errMsg = '⚠️ Incorrect email or password. Please try again.';
            if (code === 404 || raw.includes('not registered') || raw.includes('not found'))
                errMsg = '⚠️ No account found with this email. Please sign up first.';
            if (code === 403 || raw.includes('inactive'))
                errMsg = '⚠️ Your account has been deactivated. Please contact support.';
            // Last, so it wins over the 403 branch: an expired/used Turnstile
            // token is also a 403 and is not a deactivated account.
            if (raw.includes('human verification'))
                errMsg = '⚠️ The security check on this page expired. Please refresh the page and sign in again.';

            setError(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const stepLabels = ['Account Info', 'Verify OTP', 'Set Password'];

    return (
        <div className="auth-container">
            <Helmet>
                <title>LPU Faculty Access | TeachGenie™.ai</title>
                <meta name="description" content="Claim your exclusive LPU Faculty access to TeachGenie™.ai" />
            </Helmet>

            <div className="auth-background">
                <div className="gradient-1"></div>
                <div className="gradient-2"></div>
            </div>

            <div className="auth-wrapper">
                <div className="auth-card">
                    {/* Header */}
                    <div className="auth-header">
                        <div className="auth-logo">
                            <picture>
                                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                                <img
                                    src="/TechGenieMascot.png"
                                    alt="TeachGenie™.ai"
                                    className="auth-logo-image"
                                    style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }}
                                />
                            </picture>
                        </div>
                        <h1 style={{ color: '#1e3a8a', fontSize: '24px', fontWeight: '800', marginTop: '10px' }}>
                            LPU Faculty Portal
                        </h1>
                        <p className="auth-subtitle">Claim your Silver Tier access</p>
                    </div>

                    {/* Step Progress Indicator */}
                    {mode === 'register' && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0',
                            marginBottom: '30px',
                        }}>
                            {stepLabels.map((label, i) => {
                                const stepNum = i + 1;
                                const isActive = step === stepNum;
                                const isDone = step > stepNum;
                                return (
                                    <React.Fragment key={stepNum}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                                            <div style={{
                                                width: '38px',
                                                height: '38px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: isDone ? '#10b981' : isActive ? '#1e3a8a' : '#e2e8f0',
                                                color: isDone || isActive ? 'white' : '#94a3b8',
                                                fontWeight: '700',
                                                fontSize: '14px',
                                                transition: 'all 0.3s ease',
                                            }}>
                                                {isDone ? '✓' : stepNum}
                                            </div>
                                            <span style={{
                                                fontSize: '11px',
                                                color: isActive ? '#1e3a8a' : isDone ? '#10b981' : '#94a3b8',
                                                fontWeight: isActive ? '700' : '500',
                                            }}>{label}</span>
                                        </div>
                                        {i < stepLabels.length - 1 && (
                                            <div style={{
                                                height: '2px',
                                                width: '40px',
                                                background: step > stepNum ? '#10b981' : '#e2e8f0',
                                                marginBottom: '22px',
                                            }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    )}

                    <div className="auth-form-container">
                        {error && (
                            <div style={{ padding: '12px 16px', marginBottom: '20px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px' }}>
                                ⚠️ {error}
                            </div>
                        )}
                        {success && (
                            <div style={{ padding: '12px 16px', marginBottom: '20px', backgroundColor: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#16a34a', fontSize: '14px' }}>
                                ✅ {success}
                            </div>
                        )}

                        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: '24px' }}>
                            <button
                                onClick={() => { navigate('/lpu/register'); setError(''); setSuccess(''); }}
                                style={{ flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: mode === 'register' ? '2px solid #1e3a8a' : '2px solid transparent', color: mode === 'register' ? '#1e3a8a' : '#64748b', fontWeight: mode === 'register' ? '700' : '500', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s ease' }}
                            >
                                First Time Setup
                            </button>
                            <button
                                onClick={() => { navigate('/lpu/login'); setError(''); setSuccess(''); }}
                                style={{ flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: mode === 'login' ? '2px solid #1e3a8a' : '2px solid transparent', color: mode === 'login' ? '#1e3a8a' : '#64748b', fontWeight: mode === 'login' ? '700' : '500', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s ease' }}
                            >
                                Already claimed? Login
                            </button>
                        </div>

                        {mode === 'login' && (
                            <form onSubmit={handleLogin} className="auth-form">
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        LPU Email Address
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ position: 'relative', flex: 1 }}>
                                            <Mail size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                            <input
                                                type="text"
                                                value={emailPrefix}
                                                onChange={(e) => handleEmailPrefixChange(e.target.value)}
                                                onBlur={() => setTimeout(() => setShowSuggestion(false), 150)}
                                                placeholder="username"
                                                required
                                                style={{ width: '100%', padding: '12px 14px 12px 40px', borderRadius: '8px 0 0 8px', border: '1.5px solid #e2e8f0', borderRight: 'none', fontSize: '14px', boxSizing: 'border-box' }}
                                            />
                                            {showSuggestion && (
                                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100, background: '#fff', border: '1.5px solid #1e3a8a', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '200px', overflow: 'hidden' }}>
                                                    <button type="button" onClick={() => setShowSuggestion(false)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#1e3a8a', fontWeight: '600', textAlign: 'left' }}>
                                                        <Mail size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                                        {emailPrefix}<span style={{ color: '#f59e0b' }}>@lpu.co.in</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ 
                                            padding: '12px 16px', 
                                            background: '#f8fafc', 
                                            border: '1.5px solid #e2e8f0', 
                                            borderRadius: '0 8px 8px 0', 
                                            color: '#64748b', 
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            boxSizing: 'border-box',
                                            height: '46.5px', // Match input height
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            {lpuDomain}
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Password
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <Lock size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                        <input
                                            type={showLoginPassword ? 'text' : 'password'}
                                            placeholder="Enter your password"
                                            value={loginPassword}
                                            onChange={(e) => setLoginPassword(e.target.value)}
                                            required
                                            style={{ width: '100%', padding: '12px 40px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                                        />
                                        <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                                            {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{ width: '100%', padding: '14px', background: loading ? '#94a3b8' : 'linear-gradient(135deg, #1e3a8a, #f97316)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                >
                                    {loading ? 'Logging in...' : <><Lock size={18} /> Login securely <ArrowRight size={18} /></>}
                                </button>
                                
                                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                    <button type="button" onClick={() => setMode('register')} style={{ background: 'none', border: 'none', color: '#1e3a8a', fontWeight: '600', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>
                                        Forgot your password? Reset it here.
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* ── STEP 1: Name & Email Prefix ── */}
                        {mode === 'register' && step === 1 && (
                            <form onSubmit={handleSendOtp} className="auth-form">
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Full Name
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <User size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                        <input
                                            type="text"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            placeholder="Professor Name"
                                            required
                                            style={{ width: '100%', padding: '12px 14px 12px 40px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        LPU Email Address
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ position: 'relative', flex: 1 }}>
                                            <Mail size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                            <input
                                                type="text"
                                                value={emailPrefix}
                                                onChange={(e) => handleEmailPrefixChange(e.target.value)}
                                                onBlur={() => setTimeout(() => setShowSuggestion(false), 150)}
                                                placeholder="username"
                                                required
                                                style={{ width: '100%', padding: '12px 14px 12px 40px', borderRadius: '8px 0 0 8px', border: '1.5px solid #e2e8f0', borderRight: 'none', fontSize: '14px', boxSizing: 'border-box' }}
                                            />
                                            {showSuggestion && (
                                                <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 100, background: '#fff', border: '1.5px solid #1e3a8a', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: '200px', overflow: 'hidden' }}>
                                                    <button type="button" onClick={() => setShowSuggestion(false)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#1e3a8a', fontWeight: '600', textAlign: 'left' }}>
                                                        <Mail size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
                                                        {emailPrefix}<span style={{ color: '#f59e0b' }}>@lpu.co.in</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ 
                                            padding: '12px 16px', 
                                            background: '#f8fafc', 
                                            border: '1.5px solid #e2e8f0', 
                                            borderRadius: '0 8px 8px 0', 
                                            color: '#64748b', 
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            boxSizing: 'border-box',
                                            height: '46.5px', // Match input height
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            {lpuDomain}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    style={{ width: '100%', padding: '14px', background: loading ? '#94a3b8' : 'linear-gradient(135deg, #1e3a8a, #f97316)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                >
                                    {loading ? 'Processing...' : <><ShieldCheck size={18} /> Confirm & Get Verification Link</>}
                                </button>
                            </form>
                        )}

                        {/* ── STEP 2: OTP ── */}
                        {mode === 'register' && step === 2 && (
                            <form onSubmit={handleVerifyOtp} className="auth-form">
                                <p style={{ color: '#374151', fontSize: '14px', marginBottom: '20px', textAlign: 'center' }}>
                                    Enter the 6-digit verification code sent to <strong>{getFullEmail()}</strong>
                                </p>
                                <OTPInput value={otp} onChange={setOtp} error={null} disabled={loading} />
                                
                                <button
                                    type="submit"
                                    disabled={loading || otp.length !== 6}
                                    style={{ width: '100%', padding: '14px', background: loading || otp.length !== 6 ? '#94a3b8' : 'linear-gradient(135deg, #1e3a8a, #f97316)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: loading || otp.length !== 6 ? 'not-allowed' : 'pointer', marginTop: '20px' }}
                                >
                                    {loading ? 'Verifying...' : 'Verify OTP'}
                                </button>
                                
                                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                    <button type="button" onClick={handleResendOtp} disabled={loading} style={{ background: 'none', border: 'none', color: '#f59e0b', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                                        Didn't receive it? Resend OTP
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* ── STEP 3: Password Setup ── */}
                        {mode === 'register' && step === 3 && (
                            <form onSubmit={handleResetPassword} className="auth-form">
                                <div className="form-group" style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Set Secure Password
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <Lock size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                        <input
                                            type={showNewPassword ? 'text' : 'password'}
                                            placeholder="Min 8 characters"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            minLength={8}
                                            style={{ width: '100%', padding: '12px 40px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                                        />
                                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                                            {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group" style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                                        Confirm Password
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <KeyRound size={16} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="Re-enter password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            minLength={8}
                                            style={{ width: '100%', padding: '12px 40px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                                        />
                                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !newPassword || newPassword !== confirmPassword}
                                    style={{ width: '100%', padding: '14px', background: (loading || !newPassword || newPassword !== confirmPassword) ? '#94a3b8' : 'linear-gradient(135deg, #1e3a8a, #f97316)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: (loading || !newPassword || newPassword !== confirmPassword) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                >
                                    {loading ? 'Finalizing...' : <><Lock size={18} /> Enter Dashboard <ArrowRight size={18} /></>}
                                </button>
                            </form>
                        )}
                    </div>
                </div>

                <div className="auth-usp">
                    <h2 className="usp-title" style={{ textAlign: 'center' }}>
                        <span className="usp-title-navy" style={{ display: 'block', fontSize: '0.85em' }}>Revolutionize Teaching with</span>
                        <span className="usp-title-navy">Teach</span>
                        <span className="usp-title-gold">Genie</span>
                        <span className="usp-title-gold" style={{ fontSize: '0.7em', fontWeight: '900', marginLeft: '2px', verticalAlign: 'super' }}>™</span>
                        <span className="usp-title-navy">.ai</span>
                    </h2>
                    <h3 style={{ color: '#000000', fontSize: '1.25rem', marginTop: '0', marginBottom: '24px', fontWeight: '500', textAlign: 'center' }}>
                        Your AI teaching co-instructor
                    </h3>

                    <div className="usp-energy-grid">
                        <div className="usp-energy-card traditional">
                            <h4>Traditional Workflow</h4>
                            <div className="usp-energy-row">
                                <span>1 Lecture</span>
                                <strong>approx 12 prompts</strong>
                            </div>
                            <div className="usp-energy-row">
                                <span>1 Prompt</span>
                                <strong>0.34 kWh</strong>
                            </div>
                            <div className="usp-energy-row total">
                                <span>Per Course</span>
                                <strong>163.2 kWh</strong>
                            </div>
                        </div>

                        <div className="usp-energy-card teachgenie">
                            <div className="card-header-with-icon">
                                <Leaf size={20} className="sustainability-icon leaf-3d" />
                                <h4 className="workflow-title">
                                    <span className="teach-text">Teach</span>
                                    <span className="genie-text">Genie</span>
                                    <span className="tm-text">™</span>
                                    <span className="workflow-label">Workflow</span>
                                </h4>
                            </div>
                            <div className="usp-energy-row">
                                <span>1 Lecture</span>
                                <strong>1 structured generation</strong>
                            </div>
                            <div className="usp-energy-row">
                                <span>Per Lecture</span>
                                <strong>0.34 kWh</strong>
                            </div>
                            <div className="usp-energy-row total">
                                <span>Per Course</span>
                                <strong>13.6 kWh</strong>
                            </div>
                        </div>
                    </div>

                    <div className="usp-impact-strip">
                        <div className="impact-chip"><Gauge size={16} /> 91.7% fewer prompts</div>
                        <div className="impact-chip"><Leaf size={16} /> 149.6 kWh saved per course</div>
                    </div>

                    <div className="usp-feature-cloud">
                        <span className="usp-feature-pill">No prompt juggling</span>
                        <span className="usp-feature-pill">Scenario quizzes</span>
                        <span className="usp-feature-pill">Buckle Up points</span>
                        <span className="usp-feature-pill">Trends and News</span>
                        <span className="usp-feature-pill">Anchor slides ppt</span>
                        <span className="usp-feature-pill">student handout pdf</span>
                        <span className="usp-feature-pill">RBT level mapping</span>
                        <span className="usp-feature-pill">In-class exercises</span>
                        <span className="usp-feature-pill">Visual resources</span>
                        <span className="usp-feature-pill">All in one framework</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
